import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiClock } from 'react-icons/fi';
import SEOPageLayout from '../components/SEOPageLayout';
import { blogPosts, SITE_URL } from '../config/seo-data';

const BlogPost = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) {
    return <Navigate to="/" replace />;
  }

  const otherPosts = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <SEOPageLayout
      title={`${post.title} - Lasmari Chauffeurs`}
      description={post.excerpt}
      keywords={`corfu transfer guide, ${post.title.toLowerCase()}, corfu transportation`}
      canonical={`${SITE_URL}/blog/${post.slug}`}
    >
      <section className="relative py-24 lg:py-32 bg-[#faf7f6]">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-4 mb-4">
            <span className="bg-[#fbeceb] text-[#ba1609] text-xs px-3 py-1 rounded-full uppercase tracking-wider">{post.category}</span>
            <span className="text-[#5b4d4f] text-sm flex items-center gap-1"><FiClock className="w-4 h-4" /> {post.readTime}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl lg:text-5xl font-light text-[#1a1012] mb-4 leading-tight">
            {post.title}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-[#5b4d4f] text-lg font-light">
            {post.excerpt}
          </motion.p>
        </div>
      </section>

      <article className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {post.sections.map((section, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="mb-10"
            >
              <h2 className="text-xl lg:text-2xl font-light text-[#1a1012] mb-4">{section.heading}</h2>
              <p className="text-[#5b4d4f] leading-relaxed">{section.content}</p>
            </motion.div>
          ))}

          <div className="mt-12 bg-[#fbeceb] border border-black/10 rounded-xl p-6 text-center">
            <p className="text-[#1a1012] font-medium mb-2">Need a Corfu Transfer?</p>
            <p className="text-[#5b4d4f] text-sm mb-4">Book online in 60 seconds with instant confirmation.</p>
            <Link to="/booking" className="inline-block bg-[#ba1609] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#8e1107] transition-colors">
              Book Now
            </Link>
          </div>
        </div>
      </article>

      {otherPosts.length > 0 && (
        <section className="py-16 bg-[#faf7f6]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-light text-[#1a1012] mb-8">More Corfu Guides</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {otherPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="bg-white border border-black/10 rounded-lg p-5 shadow-sm hover:border-[#ba1609]/30 hover:shadow-md transition-all group">
                  <span className="text-xs text-[#ba1609] uppercase tracking-wider">{p.category}</span>
                  <h3 className="text-[#1a1012] font-medium mt-2 group-hover:text-[#ba1609] transition-colors text-sm">{p.title}</h3>
                  <p className="text-[#5b4d4f] text-xs mt-2 line-clamp-2">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </SEOPageLayout>
  );
};

export default BlogPost;
